import React from 'react'
import { css } from '@emotion/core'
import styled from '@emotion/styled'
import Link from 'next/link'
import { withTranslation } from '~/i18n'
import getUrlFromLanguage from '~/utils/i18n/getUrlFromLanguage'
import {
  H4,
  MenuButton,
  color,
  space,
  transition,
  device,
  Image,
} from '@ticketswap/solar'
import { WidePageLayout } from '~/components/PageLayout'
import NativeMenuDropdown from './NativeMenuDropdown'
import { items } from './menu-items'
import img from './how-it-works.jpg'
import { useLanguage } from '../LanguageContext'
import { AppStoreLink, GooglePlayLink } from '../AppStoreBadges'
import { Apple, Android } from '@ticketswap/comets'
import { useMutation } from '@apollo/react-hooks'
import CHANGE_USER_LOCALE from '~/graphql/mutations/changeUserLocale'
import { useViewer } from '~/components/ViewerContext'

const Container = styled.footer`
  background-color: white;
  padding-top: ${space[32]};
  border-top: 1px solid ${color.spaceLightest};
  color: ${color.space};
`

const List = styled.ul`
  display: grid;
  grid-gap: ${space[4]};
`

const HorizontalList = styled(List)`
  display: flex;
  flex-direction: row;
`

const IconWrapper = styled.span`
  display: inline-block;
  margin-right: ${space[8]};
  transform: translate3d(-2px, -2px, 0);
`

const navLinkStyles = css`
  color: ${color.spaceMedium};
  transition: color ${transition};
  display: flex;
  white-space: nowrap;
  align-items: center;

  &:hover,
  &:focus {
    color: ${color.space};
  }
`

const StyledAppStoreLink = styled(AppStoreLink)`
  ${navLinkStyles};
`

const StyledGooglePlayLink = styled(GooglePlayLink)`
  ${navLinkStyles};
`

const NavLink = styled.a`
  ${navLinkStyles};
`

const StyledH4 = styled(H4)`
  margin-bottom: ${space[16]};
`

const Appendix = styled.div`
  border-top: 1px solid ${color.spaceLightest};
  padding-top: ${space[32]};
  padding-bottom: ${space[32]};
`

const Grid = styled.div`
  padding-bottom: ${space[32]};

  @media ${device.tablet} {
    padding-top: ${space[24]};
    padding-bottom: ${space[64]};
    display: flex;
    flex-direction: row;
  }
`

const IslandAlpha = styled.div`
  margin-bottom: ${space[32]};

  @media ${device.tablet} {
    width: ${100 / 3}%;
    margin-bottom: 0;
    padding-right: ${space[8]};
  }
`

const IslandGamma = styled.div`
  @media ${device.tablet} {
    width: ${100 / 3}%;
    padding-left: ${space[8]};
  }
`

const IslandDelta = styled.div`
  @media ${device.tablet} {
    width: ${100 / 3}%;
    display: none;
  }
`

const NativeMenuDropdownWrapper = styled.div`
  @media ${device.tablet} {
    display: none;
  }
`

const PrimaryNavWrapper = styled.div`
  display: none;

  @media ${device.tablet} {
    display: block;
  }
`

const NavWrapper = styled.div`
  margin-bottom: ${space[32]};
`

const IslandBeta = styled.div`
  ${NavWrapper}:first-of-type {
    display: none;
  }

  @media ${device.tablet} {
    width: ${100 / 3}%;
    padding-left: ${space[8]};
    padding-right: ${space[8]};

    ${NavWrapper}:first-of-type {
      display: block;
    }
  }
`

const VideoWrapper = styled.div`
  margin-bottom: ${space[32]};

  @media ${device.tablet} {
    margin-bottom: 0;
  }
`

const MobileSocialNav = styled.nav`
  display: flex;
  justify-content: center;
`

const AppendixGrid = styled.div`
  display: flex;
`

const AppendixIslandAlpha = styled.div`
  display: none;

  @media ${device.tablet} {
    display: block;
    width: 50%;
  }
`

const LegalNavItem = styled.li`
  margin-right: ${space[24]};
`

const AppendixIslandBeta = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;

  @media ${device.tablet} {
    width: 50%;
  }
`

const SiteFooter = ({ t }) => {
  const { viewer } = useViewer()
  const language = useLanguage()
  const [changeUserLocale] = useMutation(CHANGE_USER_LOCALE)

  return (
    <Container data-hide-feedback-widget="true">
      <WidePageLayout>
        <Grid>
          <IslandAlpha>
            <StyledH4>{t('footer.primary.title')}</StyledH4>
            <nav aria-label="Primary navigation">
              <NativeMenuDropdownWrapper>
                <NativeMenuDropdown t={t} />
              </NativeMenuDropdownWrapper>

              <PrimaryNavWrapper>
                <List>
                  {items.primary.map(item => (
                    <li key={item.title}>
                      {item.url ? (
                        <NavLink href={item.url}>
                          {item.icon && <IconWrapper>{item.icon}</IconWrapper>}
                          {t(item.title)}
                        </NavLink>
                      ) : (
                        <Link href={item.href} as={item.as} passHref>
                          <NavLink>
                            {item.icon && (
                              <IconWrapper>{item.icon}</IconWrapper>
                            )}
                            {t(item.title)}
                          </NavLink>
                        </Link>
                      )}
                    </li>
                  ))}
                </List>
              </PrimaryNavWrapper>
            </nav>
          </IslandAlpha>

          <IslandBeta>
            <NavWrapper>
              <StyledH4>{t('footer.social.title')}</StyledH4>
              <nav aria-label="Social channels">
                <List>
                  {items.social.map(item => (
                    <li key={item.title}>
                      <NavLink href={item.url}>
                        {item.icon && <IconWrapper>{item.icon}</IconWrapper>}
                        {item.title}
                      </NavLink>
                    </li>
                  ))}
                </List>
              </nav>
            </NavWrapper>

            <NavWrapper>
              <StyledH4>{t('footer.apps.title')}</StyledH4>
              <nav aria-label="Our apps">
                <List>
                  <li>
                    <StyledAppStoreLink trackingContext="SITE_FOOTER">
                      <IconWrapper>
                        <Apple size={24} />
                      </IconWrapper>
                      {t('footer.apps.iosApp')}
                    </StyledAppStoreLink>
                  </li>
                  <li>
                    <StyledGooglePlayLink trackingContext="SITE_FOOTER">
                      <IconWrapper>
                        <Android size={24} />
                      </IconWrapper>
                      {t('footer.apps.androidApp')}
                    </StyledGooglePlayLink>
                  </li>
                </List>
              </nav>
            </NavWrapper>
          </IslandBeta>

          <IslandGamma>
            <NavWrapper>
              <StyledH4>{t('footer.help.title')}</StyledH4>
              <nav aria-label="Help navigation">
                <List>
                  {items.help.map(({ href, as, icon, title }) => (
                    <li key={title}>
                      <Link as={as} href={href} passHref>
                        <NavLink>
                          {icon && <IconWrapper>{icon}</IconWrapper>}
                          {t(title)}
                        </NavLink>
                      </Link>
                    </li>
                  ))}
                </List>
              </nav>
            </NavWrapper>

            <VideoWrapper>
              <StyledH4>{t('footer.howItWorks.title')}</StyledH4>
              <a
                href="https://www.youtube.com/watch?v=6YISCKlqCS0"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Image
                  rounded
                  src={img}
                  width={1200}
                  height={675}
                  alt="How it works"
                />
              </a>
            </VideoWrapper>
          </IslandGamma>

          <IslandDelta>
            <MobileSocialNav>
              <HorizontalList>
                {items.social.map(item => (
                  <li key={item.title}>
                    <NavLink href={item.href} aria-label={item.title}>
                      {item.icon && <IconWrapper>{item.icon}</IconWrapper>}
                    </NavLink>
                  </li>
                ))}
              </HorizontalList>
            </MobileSocialNav>
          </IslandDelta>
        </Grid>
      </WidePageLayout>

      <Appendix>
        <WidePageLayout>
          <AppendixGrid>
            <AppendixIslandAlpha>
              <nav aria-label="Legal navigation">
                <HorizontalList>
                  {items.legal.map(item => (
                    <LegalNavItem key={item.title}>
                      <Link href={item.href} as={item.as} passHref>
                        <NavLink>{t(item.title)}</NavLink>
                      </Link>
                    </LegalNavItem>
                  ))}
                </HorizontalList>
              </nav>
            </AppendixIslandAlpha>
            <AppendixIslandBeta>
              {t('footer.language.title')}:&nbsp;
              <MenuButton
                items={items.locales}
                onChange={async item => {
                  if (viewer) {
                    await changeUserLocale({
                      variables: {
                        input: {
                          locale: item.value,
                        },
                      },
                    })
                  }

                  const href = `${getUrlFromLanguage(item.value)}?language=${
                    item.value
                  }`
                  window.location.assign(href)
                }}
                initialSelectedItem={
                  items.locales.find(
                    locale => locale.value.split('-')[0] === language
                  ) || items.locales[0]
                }
                dropdownPosition="top"
              />
            </AppendixIslandBeta>
          </AppendixGrid>
        </WidePageLayout>
      </Appendix>
    </Container>
  )
}

export default withTranslation('common')(SiteFooter)
